<template>
  <FullPageLoader :show="isLoading" />

  <BModal
    :model-value="modelValue"
    @update:model-value="(val) => $emit('update:model-value', val)"
    hide-footer
    title="New Unit"
  >
    <div class="create-unit-content">
      <Form
        ref="formRef"
        @submit="createUnit"
        :validation-schema="createUnitSchema"
        v-slot="{ errors }"
      >
        <div class="create-unit-main-part">
          <div class="create-unit-block">
            <TextInput
              label="ID/Name"
              background="#fff"
              name="store_name"
              :hasBoldLabel="false"
              v-model="createUnitForm.store_name"
            />
          </div>
          <div class="create-unit-block">
            <TextInput
              label="City"
              background="#fff"
              name="city"
              :hasBoldLabel="false"
              v-model="createUnitForm.city"
            />
          </div>

          <!-- <div class="create-unit-block">
            <TextInput
              label="Address 1"
              background="#fff"
              name="address_line_1"
              :hasBoldLabel="false"
              v-model="createUnitForm.address_line_1"
            />
          </div> -->

          <!-- <div class="create-unit-block">
            <TextInput
              label="Address 2"
              background="#fff"
              name="address_line_2"
              :hasBoldLabel="false"
              v-model="createUnitForm.address_line_2"
            />
          </div> -->

          <!-- <div class="create-unit-block">
            <TextInput
              label="Postal Code"
              background="#fff"
              name="postal_code"
              :hasBoldLabel="false"
              v-model="createUnitForm.postal_code"
            />
          </div> -->

          <!-- <div class="create-unit-block">
            <TextInput
              label="Email"
              background="#fff"
              name="email"
              :hasBoldLabel="false"
              type="email"
              v-model="createUnitForm.email"
            />
          </div> -->

          <!-- <div class="create-unit-block">
            <TextInput
              label="Phone"
              background="#fff"
              name="phone"
              :hasBoldLabel="false"
              type="tel"
              v-model="createUnitForm.phone"
            />
          </div> -->

          <div class="create-unit-block">
            <SelectInput
              label="Country"
              :options="countriesDropdownOptions"
              :tryToFindNameInOptionsOnFirstLoad="true"
              :hasBoldLabel="false"
              name="country"
              :reduce="(option) => option.value"
              v-model="createUnitForm.country_code"
              @update:model-value="
                (val) => handleCountryChange(val)
              "
            />
          </div>

          <div class="create-unit-block">
            <SelectInput
              label="State/Province"
              :options="
                COUNTRIES_LIST.find(
                  ({ code }) => code === createUnitForm.country_code
                )?.states?.map((province) => {
                  return {
                    text: province,
                    value: province,
                  };
                }) ?? []
              "
              :tryToFindNameInOptionsOnFirstLoad="true"
              :hasBoldLabel="false"
              :disabled="
                !checkIsThisCountryCodeHasStates(createUnitForm.country_code)
              "
              name="province"
              :reduce="(option) => option.value"
              v-model="createUnitForm.province"
            />
          </div>
          <div class="create-unit-block">
            <SelectInput
              label="Languages"
              :multiple="true"
              :options="LANGUAGES_LIST"
              :tryToFindNameInOptionsOnFirstLoad="true"
              :hasBoldLabel="false"
              name="language"
              :reduce="(option) => option.value"
              v-model="createUnitForm.languages"
            />
          </div>
          <div class="create-unit-block">
            <SelectInput
              :options="userStore.hqActiveUsersDropdownOptions"
              :tryToFindNameInOptionsOnFirstLoad="true"
              :hasBoldLabel="false"
              appendToBody
              v-model="createUnitForm.business_coach_id"
              label="Support Representative"
              :reduce="(option) => option.value"
              name="business_coach_id"
            />
          </div>
        </div>

        <div class="create-unit-bottom-part">
          <CustomButton type="submit" button-type="blue">
            Create Unit
          </CustomButton>
        </div>
      </Form>
    </div>
  </BModal>
</template>

<script setup>
import { Form } from "vee-validate";
import {
  baseEmailValidation,
  baseStringValidation,
} from "@/validation/base.validations";
import * as yup from "yup";
import { array, object, string, ref as yupRef } from "yup";
import { useUserStore } from "@/stores/user";
import { useUnitStore } from "@/stores/unit";

import { COUNTRIES_LIST, LANGUAGES_LIST } from "@/constants/countries";
import { FORM_ERROR_MESSAGES } from "@/validation";
import { useToast } from "vue-toastification";
import { getUserNameAndFallbacksFromRawData } from "@/utils/utils";

import api from "@/api";

const userStore = useUserStore();
const unitsStore = useUnitStore();
const toast = useToast();

const emit = defineEmits(["update:model-value", "unitCreated"]);

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const isLoading = ref();
const formRef = ref(null);

const initialFormValues = {
  store_name: "",
  city: "",
  province: "",
  address_line_1: "",
  address_line_2: "",
  country_code: "",
  postal_code: "",
  users: [],
  business_coach_id: "",
  languages: [],
};

const createUnitForm = ref(structuredClone(initialFormValues));

const createUnitSchema = yup.object({
  store_name: yup
    .string()
    .required(FORM_ERROR_MESSAGES.REQUIRED)
    .test(
      "unique-name",
      "Unit with this name already exists",
      (value) =>
        !Object.values(unitsStore.allDict)
          .map(({ store_name }) => store_name)
          .includes(value)
    ),
  city: yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
});

const countriesDropdownOptions = computed(() => {
  return COUNTRIES_LIST.map((country) => {
    return {
      text: country.name,
      value: country.code,
    };
  });
});

async function createUnit() {
  isLoading.value = true;
  try {
    const res = await api.unit.createUnit({
      store_id: createUnitForm.value.store_name || " ",
      store_name: createUnitForm.value.store_name || " ",
      city: createUnitForm.value.city || " ",
      country_code: createUnitForm.value.country_code || " ",
      province: createUnitForm.value.province || " ",
      organization_id: userStore.activeOrganization || " ",
      address_line_1: createUnitForm.value.address_line_1 || " ",
      address_line_2: createUnitForm.value.address_line_2 || " ",
      email: createUnitForm.value.email || " ",
      is_active: true,
      phone: createUnitForm.value.phone || " ",
      postal_code: createUnitForm.value.postal_code || " ",
      business_coach_id: createUnitForm.value.business_coach_id || null,
      languages: createUnitForm.value.languages || [],
    });
    emit("unitCreated", res.data.id);
    closeAddNewUnitModal();
    toast.success("Succesfully created new Unit");
    unitsStore.fetchAll();
  } catch {
  } finally {
    isLoading.value = false;
  }
}

function closeAddNewUnitModal() {
  createUnitForm.value = {
    store_name: "",
    city: "",
    province: "",
    address_line_1: "",
    address_line_2: "",
    country_code: "",
    postal_code: "",
    users: [],
    business_coach_id: "",
    languages: [],
  };
  emit("update:model-value", false);
  isLoading.value = false;
}

const checkIsThisCountryCodeHasStates = (country_code) => {
  if (country_code) {
    const data = COUNTRIES_LIST.find(({ code }) => code === country_code);

    if (data?.states && data?.states?.length) {
      return true;
    }
  }
  return false;
};

const handleCountryChange = (newValue) => {
  // Reset the province field whenever the country changes
  createUnitForm.value.province = "";
};

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      createUnitForm.value = structuredClone(initialFormValues);
      formRef.value.resetForm();
    }
  }
);
</script>

<style lang="scss" scoped>
.create-unit-block {
  margin-bottom: 32px;
}

.create-unit-bottom-part {
  width: fit-content;
  margin-left: auto;
}
</style>
